import Pagination from '../../components/Common/Pagination';
import { TableLoader } from '../../components/Common/Loader';
import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchCategories,
  setActiveTab,
  setSearchQuery,
  setCurrentPage,
  setLimit,
} from '../../store/categoriesSlice';
import type { CategoryItem } from '../../api/categories';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

export const CategoriesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    categories,
    summary,
    pagination,
    loading,
    error,
    activeTab,
    searchQuery,
    currentPage,
    limit: entriesLimit,
  } = useAppSelector((state) => state.categories);

  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [_editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    hsnCode: '',
    size: '',
    isSpecial: false,
    status: 'Active' as 'Active' | 'Inactive',
    image: 'Not Available',
  });

  // Fetch categories from API on mount and filter changes
  useEffect(() => {
    dispatch(
      fetchCategories({
        page: currentPage,
        limit: entriesLimit,
        search: searchQuery,
        status: activeTab,
      })
    );
  }, [dispatch, activeTab, currentPage, entriesLimit, searchQuery]);

  // Helper to determine active status across different API field formats
  const getIsActive = (category: CategoryItem): boolean => {
    if (typeof category.isActive === 'boolean') return category.isActive;
    if (typeof category.is_active === 'boolean') return category.is_active;
    if (category.is_active === 1 || category.status === 1) return true;
    if (category.is_active === 0 || category.status === 0) return false;
    return true;
  };

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    if (dateStr.includes('T')) {
      const parts = dateStr.split('T');
      const time = parts[1].split('.')[0];
      return `${parts[0]} ${time}`;
    }
    return dateStr;
  };

  // Derived Counts
  const activeCounts = summary
    ? summary.active_count
    : categories.filter((c) => getIsActive(c)).length;
  const inactiveCounts = summary
    ? summary.inactive_count
    : categories.filter((c) => !getIsActive(c)).length;

  // Filtering Logic (local fallback or server filtered)
  const filteredCategories = useMemo(() => {
    if (!categories) return [];
    return categories.filter((category) => {
      const active = getIsActive(category);
      if (activeTab === 'Active' && !active) return false;
      if (activeTab === 'Inactive' && active) return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const hsnCodeStr = category.hsnCode || category.hsn_code || '';
        return (
          category.name?.toLowerCase().includes(query) ||
          hsnCodeStr.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [categories, activeTab, searchQuery]);

  const handleOpenAdd = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      hsnCode: '',
      size: '',
      isSpecial: false,
      status: 'Active',
      image: 'Not Available',
    });
    setView('add');
  };

  const handleOpenEdit = (category: CategoryItem) => {
    setEditingCategory(category);
    const hsnVal = category.hsnCode || category.hsn_code || '';
    setFormData({
      name: category.name || '',
      hsnCode: hsnVal === '-' ? '' : hsnVal,
      size: category.size || '',
      isSpecial: category.isSpecial || category.is_special || false,
      status: getIsActive(category) ? 'Active' : 'Inactive',
      image: category.image || 'Not Available',
    });
    setView('edit');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Category Name is required.');
      return;
    }
    // Re-fetch after add/edit
    dispatch(fetchCategories());
    setView('list');
  };

  // CSV Download Action
  const handleDownloadCSV = () => {
    const headers = ['ID', 'Name', 'HSN Code', 'Status', 'Created', 'Modified'];
    const rows = filteredCategories.map((category) => [
      category.id,
      category.name || '',
      category.hsnCode || category.hsn_code || '-',
      getIsActive(category) ? 'Active' : 'Inactive',
      category.createdAt || category.created_at || category.created || '',
      category.updatedAt || category.updated_at || category.modified || '',
    ]);

    exportToCSV(`gowardhan_categories_${activeTab.toLowerCase()}`, headers, rows);
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setView('list')}
              className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
              title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
              {view === 'edit' ? 'Edit Categories' : 'Add Categories'}
            </h1>
          </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-3xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Category Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Category Name*</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800"
              />
            </div>

            {/* HSN Code */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">HSN Code</label>
              <input
                type="text"
                value={formData.hsnCode}
                onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800"
              />
            </div>


            {/* Size Dropdown */}
            {/* <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Type</label>
              <div className="relative">
                <select
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  className="w-full bg-[#F5F5F5] border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs appearance-none"
                >
                  <option value="" className='text-[#828282]'>SKU Unit</option>
                  <option value="100g">100g</option>
                  <option value="200g">200g</option>
                  <option value="500 Ml">500 Ml</option>
                  <option value="1 Ltr">1 Ltr</option>
                  <option value="5 Ltrs">5 Ltrs</option>
                  <option value="5 Kg">5 Kg</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div> */}

            {/* SKU Image Upload */}
            {/* <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">SKU Image</label>
              <div className="flex items-center border border-[#828282] rounded-2xl overflow-hidden bg-white">
                <button
                  type="button"
                  className="go_erp-bg text-white text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Upload
                </button>
                <input
                  type="file"
                  className="flex-1 bg-transparent px-3 text-xs text-slate-500 outline-none cursor-pointer font-semibold"
                />
                <button
                  type="button"
                  className="bg-[#FFF8E1] text-slate-700 hover:bg-[#FFF3CD] text-xs font-bold py-2.5 px-4 transition-colors"
                >
                  Browse
                </button>
              </div>
            </div> */}


            {/* Status Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Status</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  className="w-full bg-[#F5F5F5] border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 appearance-none cursor-pointer"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

        </form>
          {/* Submit Button */}
        <div className="flex justify-center">
            <button
              type="submit"
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20"
            >
              Submit
            </button>
          </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Categories
          </h1>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => dispatch(setActiveTab(tab))}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${isActive
                    ? 'go_erp-bg text-white shadow-sm'
                    : 'text-slate-650 hover:text-slate-900'
                  }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Categories
          </button>

          <CSVButton onClick={handleDownloadCSV} />
        </div>
      </div>

      {/* Counts */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold">{inactiveCounts}</span></span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#ECECF2] overflow-hidden">

        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => dispatch(setLimit(Number(e.target.value)))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Table Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-44 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {error && !loading && (
          <div className="py-6 px-6 text-center text-xs font-semibold text-red-500 bg-red-50">
            Error: {error}
          </div>
        )}

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">HSN Code</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Created</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Modified</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={7} text="Loading..." />
              ) : filteredCategories.length > 0 ? (
                  filteredCategories.map((category) => (
                    <tr key={category.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-4 text-xs text-[#828282]">{category.id}</td>
                      <td className="px-4 py-4 text-xs text-[#828282] font-semibold">{category.name}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{category.hsnCode || category.hsn_code || '-'}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${getIsActive(category) ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                          {getIsActive(category) ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{formatDate(category.createdAt || category.created_at || category.created)}</td>
                      <td className="px-4 py-4 text-xs text-[#828282]">{formatDate(category.updatedAt || category.updated_at || category.modified)}</td>
                      <td className="px-4 py-4 text-xs">
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleOpenEdit(category)} className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg" title="View">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button onClick={() => handleOpenEdit(category)} className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg" title="Edit">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                      No categories found matching filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        {/* Footer */}
        <Pagination
          currentPage={pagination ? pagination.page : currentPage}
          totalEntries={pagination ? pagination.total_records : filteredCategories.length}
          entriesLimit={pagination ? pagination.limit : entriesLimit}
          onPageChange={(page) => dispatch(setCurrentPage(page))}
        />

      </div>
    </div>
  );
};
export default CategoriesList;
